<script>
  export let gallery;
  export let close;
  /** When true, main images use pan/zoom with a minimap; main Splide drag is disabled so it does not fight pan gestures (use arrows, pagination, or thumbnails). */
  export let enableZoomMinimap = false;

  import { onDestroy, onMount, tick } from "svelte";
  import { focusTrap } from "svelte-focus-trap";

  import CloseButton from "./CloseButton.svelte";
  import GalleryZoomViewport from "./GalleryZoomViewport.svelte";

  import { Splide, SplideSlide } from "@splidejs/svelte-splide";
  import "@splidejs/splide/dist/css/splide.min.css";

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  let main;
  let thumbs;
  let isSingleImage = gallery.length === 1 ? true : false;

  /** Bumped on Splide `moved` so each GalleryZoomViewport resets pan/zoom when changing slides */
  let resetEpoch = 0;

  $: mainOptions = {
    type: "loop",
    pagination: true,
    rewind: true,
    perMove: 1,
    perPage: 1,
    arrows: !isSingleImage,
    drag: !enableZoomMinimap,
  };

  const thumbsOptions = {
    type: "slide",
    rewind: true,
    gap: "1rem",
    pagination: false,
    fixedWidth: 110,
    fixedHeight: 70,
    cover: true,
    isNavigation: true,
    updateOnMove: true,
    arrows: false,
  };
  onMount(async () => {
    await tick();
    if (main && thumbs) {
      main.sync(thumbs.splide);
    }
    await tick();
    if (enableZoomMinimap && main?.splide) {
      main.splide.on("moved", () => {
        resetEpoch++;
      });
    }
  });

  let visible = true;
  function closeGallery() {
    visible = false;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (close) {
      close();
    }
  }

  onDestroy(() => {
    if (main) {
      main.destroy();
    }
    if (thumbs) {
      thumbs.destroy();
    }
  });
</script>

{#if visible}
  <div class="gallery" use:focusTrap>
    <CloseButton close={closeGallery} />

    <div class="container">
      <Splide options={mainOptions} bind:this={main}>
        {#each gallery as slide}
          <SplideSlide>
            {#if enableZoomMinimap}
              <GalleryZoomViewport
                src={slide.src}
                alt={slide.alt}
                resetEpoch={resetEpoch}
              />
            {:else}
              <img
                style="max-width: 100%; max-height: 75vh;"
                src={slide.src}
                alt={slide.alt}
              />
            {/if}
          </SplideSlide>
        {/each}
      </Splide>

      {#if !isMobile}
        {#if !isSingleImage}
          <Splide options={thumbsOptions} bind:this={thumbs}>
            {#each gallery as slide}
              <SplideSlide><img src={slide.src} alt={slide.alt} /></SplideSlide>
            {/each}
          </Splide>
        {/if}
      {/if}
    </div>
    <div class="bg" on:click={closeGallery}></div>
  </div>
{/if}

<style>
  .gallery {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    width: 100%;
    height: 100%;
  }

  .container {
    margin-top: 100px;
    text-align: center;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .bg {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    background: rgba(0, 0, 0, 0.98);
    width: 100%;
    height: 100%;
  }

  :global(.container .splide:nth-child(2) .splide__slide.is-active) {
    border: 3px solid #fff !important;
  }

  :global(.splide:nth-child(2) .splide__list) {
    justify-content: center;
  }

  :global(.splide__slide) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :global(.splide__track) {
    height: 100%;
  }

  :global(.splide--nav) {
    padding: 15px;
  }

  :global(.splide--loop) {
    height: calc(100% - 200px);
  }

  :global(.splide__slide img) {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  :global(.splide__slide .gallery-zoom-root) {
    flex-shrink: 0;
  }

  @media only screen and (max-width: 600px) {
    .container {
      margin-top: 60px;
      height: calc(100% - 60px);
    }

    :global(.splide--loop) {
      height: calc(100% - 100px);
    }

    :global(.splide__slide) {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    :global(.splide__track) {
      display: flex;
      align-items: center;
    }

    :global(.splide__list) {
      height: 100%;
      align-items: center;
    }

    :global(.splide__slide img) {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }

  :global(.splide__arrow svg) {
    fill: #fff !important;
    width: 3em !important;
    height: 3em !important;
  }

  :global(.splide__arrow) {
    background: none !important;
    width: 3em !important;
  }
</style>
